import {
  GSFileSelect,
  GSButton,
  validations,
  GSRadioGroup,
  GSSelect,
  GSImageEditor,
} from "golfstatus_react_components";
import {
  faPlus,
  faPercent,
  faDollarSign,
  faTimesCircle,
  faQrcode,
} from "@fortawesome/free-solid-svg-icons";
import OrganizationDiscountItem from "../components/discounts/organization-discount-item";
import { getDateTimeYMD } from "../helpers/Dates";

export const memberRoles = [
  { id: 1, label: "Head Coach", value: "Head Coach" },
  { id: 2, label: "Coach", value: "Coach" },
  { id: 3, label: "Member", value: "Member" },
  { id: 4, label: "Owner", value: "Owner" },
  { id: 5, label: "Manager", value: "Manager" },
  { id: 6, label: "Marketing", value: "Marketing" },
  { id: 7, label: "Director", value: "Director" },
  { id: 8, label: "Golf Professional", value: "Golf Professional" },
];

export const getOrgPages = () => {
  let pages = [
    {
      label: "Users & Permissions",
      details: "Provide others with access to manage this organization.",
      isExternal: false,
      url: "permissions",
      hidden: false,
    },
    {
      label: "Discounts",
      details: "Add and manage organization-wide discounts codes.",
      isExternal: false,
      url: "discounts",
      hidden: false,
    },
    {
      label: "Reporting",
      details: "View reporting analytics for all events.",
      isExternal: false,
      hidden: true,
      url: "",
    },
    {
      label: "Events List",
      details:
        "Customize Images and edit privacy settings for your global event list.",
      isExternal: false,
      url: "",
      hidden: true,
    },
    {
      label: "Access Lists",
      details: "Manage exclusive tournament registration access.",
      isExternal: false,
      url: "access",
      hidden: false,
    },
    {
      label: "Settings & Preferences",
      url: "settings",
      hidden: false,
    },
  ];
  return pages?.filter?.((p) => !p.hidden);
};

export const getOrgForm = (
  context,
  selectedOrganization,
  availableCurrencies
) => {
  if (context) {
    let currencyOptions = availableCurrencies.map((c) => {
      return {
        label: `${c.code} - ${c.country}`,
        value: c,
      };
    });

    let selectedCurrency = currencyOptions.find(
      (c) => c.value.id === selectedOrganization.currency.id
    );

    let form = [
      {
        type: "vertical xx-large-gap",
        fields: [
          {
            label: "Organization Name",
            value: selectedOrganization?.name,
            failedValidation: context?.validationFailed,
            placeholder: "Organization Name",
            onChange: (e) =>
              context?.updateOrganization?.(e.target.value, "name"),
            isEditable: true,
            required: true,
          },
          {
            label: "Organization Logo",
            value: (
              <GSFileSelect
                description="Tap or drag file to upload. Accepted Files: .JPG, .PNG, .GIF"
                setSelectedFiles={context?.setLogo}
                removeSourceItem={context?.removeLogo}
                sourceList={context?.getLogo?.()}
                accept=".jpg, .jpeg, .png, mp4"
                warningClicked={context?.editImage}
                editImage={context?.editImage}
                title={
                  <GSButton buttonIcon={faPlus} title="add logo"></GSButton>
                }
                ratio={2}
                minHeight={300}
                minWidth={600}
                failedValidation={context?.validationFailed}
              />
            ),
            customView: true,
            failedValidation: context?.validationFailed,
            isEditable: true,
          },
          {
            label: "Custom Identifier",
            value: selectedOrganization?.customIdentifier,
            failedValidation: context?.validationFailed,
            placeholder: "Custom Identifier",
            onChange: (e) =>
              context?.updateOrganization?.(e.target.value, "customIdentifier"),
            isEditable: true,
            required: false,
            hintText: "This is an internal value used for record keeping.",
          },
          {
            label: "Currency - Locale",
            value: (
              <GSSelect
                options={currencyOptions}
                onChange={(e) => {
                  context?.updateOrganization?.(e.value, "currency");
                }}
                selectedOption={selectedCurrency}
              />
            ),
            validation: {
              isValid: () => Boolean(selectedCurrency),
              invalidLabel: "No currency selected",
            },
            customView: true,
            failedValidation: context.validationFailed,
            isEditable: true,
            required: true,
            hintText: selectedOrganization?.isEscrowAccount
              ? "Additional currencies are available to organizations with a connected Stripe account."
              : null,
          },
        ],
      },
    ];
    return form;
  }
};

export const getOrgLogoEditForm = (context, showCroppedImage, croppedImg) => {
  return [
    {
      type: "vertical",
      fields: [
        {
          value: showCroppedImage ? (
            <img className="preview-image" src={croppedImg} alt="upload" />
          ) : (
            <GSImageEditor
              source={context?.getLogo?.()}
              minWidth={600}
              minHeight={300}
              setCroppedDataURL={context.setCroppedDataURL}
              setCroppedBlob={context.setCroppedBlob}
              resizeEnabled={true}
              ratio={2}
            ></GSImageEditor>
          ),
        },
      ],
    },
  ];
}
export const getOrgBannerImageEditForm = (context, showCroppedImage, croppedImg) => {
  return [
    {
      type: "vertical",
      fields: [
        {
          value: showCroppedImage ? (
            <img className="preview-image" src={croppedImg} alt="upload" />
          ) : (
            <GSImageEditor
              source={context?.getLogo?.()}
              setCroppedDataURL={context.setCroppedDataURL}
              setCroppedBlob={context.setCroppedBlob}
              resizeEnabled={true}
              minWidth={25}
              minHeight={25}
            ></GSImageEditor>
          ),
        },
      ],
    },
  ];
}

export const addUserForm = (context, user) => {
  if (context) {
    const { validEmail } = validations;
    let types = [
      { id: 1, label: "Organization Member", value: 1 },
      { id: 2, label: "Tournament Organizer", value: 2 },
    ];

    let details = {
      type: "vertical xx-large-gap",
      fields: [
        {
          label: "Permission Type",
          value: (
            <GSRadioGroup
              selectionChanged={(option) => {
                if (context.isEditable()) {
                  context?.updateUser?.(option, "permissionType");
                }
              }}
              selectedOption={user?.permissionType}
              options={types}
            />
          ),
          customView: true,
          validation: {
            isValid: () => user.permissionType !== undefined,
            invalidLabel: "Permission Type required",
          },
          failedValidation: context?.validationFailed,
          required: true,
        },
        {
          label: "Email Address",
          value: user?.email,
          failedValidation: context?.validationFailed,
          placeholder: "Email Address",
          onChange: (e) => context?.updateUser?.(e.target.value, "email"),
          validation: {
            isValid: validEmail,
            invalidLabel: "Not a valid email address",
          },
          isEditable: context?.isMember?.() || context?.isEditable?.(),
          required: true,
        },
      ],
    };

    let form = [{ ...details }];
    return form;
  }
};

export const addDiscountForm = (context, discount) => {
  if (context) {
    let discountLabel = {
      type: "vertical xx-large-gap",
      fields: [
        {
          label: "Discount Label",
          value: discount?.description,
          failedValidation: context?.validationFailed,
          placeholder: "Discount Label",
          onChange: (e) =>
            context?.updateDiscount?.(e.target.value, "description"),
          isEditable: context?.isEditable?.(),
          required: true,
        },
      ],
    };

    let details = {
      type: "vertical xx-large-gap",
      title: "Discount Details",
      fields: [
        {
          label: "Discount Code",
          value: discount?.code,
          failedValidation: context?.validationFailed,
          placeholder: "Discount Code (Tap Icon to Generate Code)",
          leftIcon: faQrcode,
          leftIconClick: context.generateCode,
          rightIcon: faTimesCircle,
          rightIconClick: () => {
            context.updateDiscount("", "code");
          },
          onChange: (e) => context?.updateDiscount?.(e.target.value, "code"),
          isEditable: context?.isEditable?.(),
          required: true,
        },
        {
          label: "Discount type",
          value: (
            <GSRadioGroup
              selectionChanged={(option) => {
                context?.updateDiscount?.(option, "discountType");
              }}
              selectedOption={discount?.discountType}
              options={[
                { id: 1, label: "Fixed Amount", value: 1 },
                { id: 2, label: "Percentage", value: 2 },
              ]}
            />
          ),
          customView: true,
          placeholder: "Discount Type",
          onChange: (e) => context?.updateDiscount?.(e.target.value, "type"),
          isEditable: context?.isEditable?.(),
        },
      ],
    };

    if (discount?.discountType?.id === 1) {
      details.fields.push({
        label: "Discount Amount ",
        value: discount?.discountDollarAmount,
        placeholder: "0.00",
        failedValidation: context?.validationFailed,
        leftIcon: faDollarSign,
        onChange: (e) =>
          context?.updateDiscount?.(e.target.value, "discountDollarAmount"),
        isEditable: context?.isEditable?.(),
        required: true,
      });
    } else {
      details.fields.push({
        label: "Discount Percentage ",
        value: discount?.discountPercent,
        rightIcon: faPercent,
        placeholder: "0",
        failedValidation: context?.validationFailed,
        onChange: (e) =>
          context?.updateDiscount?.(e.target.value, "discountPercent"),
        isEditable: context?.isEditable?.(),
        required: true,
      });
    }

    let usage = {
      type: "vertical xx-large-gap",
      title: "Discount Usage",
      fields: [
        {
          label: "Discount Limit",
          value: (
            <GSRadioGroup
              selectionChanged={(option) => {
                context?.updateDiscount?.(option, "discountUsage");
              }}
              selectedOption={discount?.discountUsage}
              options={[
                { id: 1, label: "Unlimited Use", value: 1 },
                { id: 2, label: "Limited Use", value: 2 },
              ]}
            />
          ),
          customView: true,
          placeholder: "Discount Type",
          isEditable: context?.isEditable?.(),
        },
      ],
    };

    if (discount?.discountUsage?.id === 2) {
      usage.fields.push({
        label: "Uses Per Code",
        value: discount?.quantity,
        failedValidation: context?.validationFailed,
        placeholder: "Uses Per Code",
        onChange: (e) => context?.updateDiscount?.(e.target.value, "quantity"),
        isEditable: context?.isEditable?.(),
        required: true,
      });
    }

    let discountActivation = {
      type: "vertical xx-large-gap",
      title: "Discount Activation",
      fields: [
        {
          label: "Activate On",
          value:
            discount?.activateOn !== ""
              ? getDateTimeYMD(discount?.activateOn)
              : "",
          type: "datetime-local",
          failedValidation: context?.validationFailed,
          onChange: (e) =>
            context?.updateDiscount?.(e.target.value, "activateOn"),
          isEditable: context?.isEditable?.(),
          required: false,
        },
        {
          label: "Deactivate On",
          value:
            discount?.deactivateOn !== ""
              ? getDateTimeYMD(discount?.deactivateOn)
              : "",
          type: "datetime-local",
          failedValidation: context?.validationFailed,
          onChange: (e) =>
            context?.updateDiscount?.(e.target.value, "deactivateOn"),
          isEditable: context?.isEditable?.(),
          required: false,
        },
      ],
    };

    let form = [
      { ...discountLabel },
      { ...usage },
      { ...details },
      { ...discountActivation },
    ];
    return form;
  }
};

export const removeDiscountForm = (context, discount) => {
  if (context) {
    let form = [
      {
        type: "vertical xx-large-gap",
        extras: (
          <div className="delete-discount">
            <div className="disclaimer">{`Are you sure you want to remove ${discount.description} from your organization? This cannot be undone.`}</div>
            <OrganizationDiscountItem {...discount} />
          </div>
        ),
      },
    ];
    return form;
  }
};
